import type {
  ReservationSlotSettings,
  ReservationSlotSettingsInput,
} from '../../../domain/reservationSlots';
import type { ReservationSlotSettingsRepository } from '../../../application/reservationSlots/ports/ReservationSlotSettingsRepository';
import { doctorApiService } from '../../../services/doctorApiService';
import { mockDelay } from './mockUtils';

/**
 * 医師一覧APIの1ページあたり取得件数
 *
 * @description
 * バックエンドの `DoctorIndexRequest` が `per_page` を最大100に制限しているため、
 * これを超える値を送ると422になる。全医師を得るにはページを送って繰り返し取得する。
 */
const DOCTORS_PER_PAGE = 100;

/**
 * 内部保持用の上書き（医師名は保持せず、読み出し時に医師一覧から解決する）
 */
interface StoredOverride {
  doctorId: number;
  templateId: string;
}

/**
 * 基本設定 Repository のモック実装
 *
 * @description
 * デフォルトテンプレートと個別上書きをメモリ上に保持する。
 * 医師名は実在する `/api/doctors` を参照して解決する（画面の医師一覧と同じ情報源）。
 * バックエンド未起動時は「医師ID:n」のフォールバック名を返す。
 *
 * @implements {ReservationSlotSettingsRepository}
 */
export class ReservationSlotSettingsRepositoryMock implements ReservationSlotSettingsRepository {
  private defaultTemplateId: string | null = 'tpl-1';
  private overrides: StoredOverride[] = [];
  private doctorNameCache: Map<number, string> | null = null;

  /**
   * 医師ID→氏名のマップを取得する（初回のみ実APIから取得しキャッシュ）
   */
  private async getDoctorNameMap(): Promise<Map<number, string>> {
    if (this.doctorNameCache) {
      return this.doctorNameCache;
    }
    const map = new Map<number, string>();
    try {
      // 全医師を得るため、最終ページに達するまでページを送って取得する
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await doctorApiService.getDoctors({
          page: currentPage,
          per_page: DOCTORS_PER_PAGE,
        });
        for (const doctor of response.data) {
          map.set(doctor.id, `${doctor.first_name} ${doctor.last_name}`.trim());
        }

        if (response.meta && response.meta.current_page < response.meta.last_page) {
          currentPage += 1;
        } else {
          hasMorePages = false;
        }
      }
    } catch {
      // バックエンド未起動などで取得に失敗した場合はフォールバック名を使う
    }
    this.doctorNameCache = map;
    return map;
  }

  /**
   * 内部の上書きを、医師名付きのドメイン設定へ変換する
   */
  private async toSettings(): Promise<ReservationSlotSettings> {
    const nameMap = await this.getDoctorNameMap();
    return {
      defaultTemplateId: this.defaultTemplateId,
      overrides: this.overrides.map((o) => ({
        doctorId: o.doctorId,
        doctorName: nameMap.get(o.doctorId) ?? `医師ID:${o.doctorId}`,
        templateId: o.templateId,
      })),
    };
  }

  /**
   * 指定テンプレートが基本設定または個別上書きから参照されているか
   * （テンプレート削除の可否判定にモック内部から使用）
   */
  isTemplateReferenced(templateId: string): boolean {
    if (this.defaultTemplateId === templateId) {
      return true;
    }
    return this.overrides.some((o) => o.templateId === templateId);
  }

  async get(): Promise<ReservationSlotSettings> {
    await mockDelay();
    return this.toSettings();
  }

  async update(input: ReservationSlotSettingsInput): Promise<ReservationSlotSettings> {
    await mockDelay();
    this.defaultTemplateId = input.defaultTemplateId;
    this.overrides = input.overrides.map((o) => ({
      doctorId: o.doctorId,
      templateId: o.templateId,
    }));
    return this.toSettings();
  }
}

export const reservationSlotSettingsRepositoryMock = new ReservationSlotSettingsRepositoryMock();
